import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import Pagination from "../Pagination/Pagination";
import FilterBar from "../FilterBar/FilterBar";
import { Edit, Trash2 } from "lucide-react";
import ConfirmDeleteModal from "../ModelUI/ConfirmDeleteModal/ConfirmDeleteModal";
import AuthService from "../../services/Auth.service";
import AddDispositionGroupModel from "./AddDispositionGroupModel";
import SkeletonLoader from "../SkeletonLoader/SkeletonLoader";

export default function DispositionGroupList() {
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [postsPerPage, setPostsPerPage] = useState(20);
  const [headers, setHeaders] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [pagination, setPagination] = useState({
    page: 1,
    size: 20,
    total: 0,
    total_pages: 0,
  });

  const validationSchema = yup.object().shape({});
  const { handleSubmit, control } = useForm({
    resolver: yupResolver(validationSchema),
  });

  const fetchDetail = async (page = 1, size = postsPerPage) => {
    try {
      setLoading(true);
      const res = await AuthService.getDispositionGroupList({ page, size });

      if (res.status === 200 && res.data) {
        setTableData(res.data.data || []);
        setPagination(res.data.pagination || {});
        setHeaders(res.data.header || []);
      } else {
        toast.error("Failed to fetch Disposition Groups");
      }
    } catch (error) {
      console.error("Error fetching Disposition Groups:", error);
      toast.error("Error loading disposition groups");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, []);

  const handleSubmitFilter = async (data) => {
    await fetchDetail(1, postsPerPage);
  };

  const handleAdd = () => {
    setSelectedItem(null);
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleSaveDispositionGroup = async (payload) => {
    try {
      let res;
      if (isEditing && selectedItem) {
        res = await AuthService.updateDispositionGroup(selectedItem.id, payload);
        if (res.status === 200) {
          toast.success(res.data?.message || "Disposition group updated successfully");
        } else {
          toast.error("Failed to update Disposition Group");
        }
      } else {
        res = await AuthService.addDispositionGroup(payload);
        if (res.status === 200 || res.status === 201) {
          toast.success(res.data?.message || "Disposition group added successfully");
        } else {
          toast.error("Failed to add Disposition Group");
        }
      }

      setShowModal(false);
      fetchDetail(pagination.page, postsPerPage);
    } catch (error) {
      console.error("Save error:", error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save Disposition Group");
    }
  };

  const handleDeleteGroup = (item) => {
    setDeleteTarget(item);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    try {
      const res = await AuthService.deleteDispositionGroup(deleteTarget.id);

      if (res.status === 200 || res.status === 204) {
        toast.success(res.data?.message || "Disposition group deleted successfully");
        fetchDetail(pagination.page, postsPerPage);
      } else {
        toast.error("Failed to delete Disposition Group");
      }
    } catch (error) {
      console.error("Error deleting Disposition Group:", error);
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error deleting Disposition Group");
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPostsPerPage(newSize);
    fetchDetail(pagination.page, newSize);
  };

  const mistableTh = {
    backgroundColor: "transparent",
    color: "rgba(43, 58, 103, 1)",
    fontFamily: "Nunito",
    fontSize: "13px",
    fontWeight: 700,
    textAlign: "center",
  };

  const tableTdStyles = {
    backgroundColor: "#ffffff",
    fontFamily: "Nunito",
    fontSize: "12px",
    fontWeight: "700",
    textAlign: "center",
    color: "#232122",
  };

  return (
    <>
      <FilterBar
          control={control}
          handleSubmit={handleSubmit}
          onSubmit={handleSubmitFilter}
          onClearFilters={() => fetchDetail(1, postsPerPage)}
          filters={[]}
          title="Disposition Group List"
          totalCount={pagination.total || 0}
          tableId="disposition-groups-table"
          isDownload={false}
          isAdd={true}
          onAdd={handleAdd}
        />

        <div className="table-container">
          {loading ? (
            <SkeletonLoader type="table" rows={8} cols={headers.length ? headers.length + 2 : 4} />
          ) : (
            <table id="disposition-groups-table" className="custom-table-container">
              <thead
                className="table-head"
                style={{ position: "sticky", top: "0", zIndex: "1" }}
              >
                <tr>
                  <th style={mistableTh}>S.No</th>
                  {headers.map((header, i) => (
                    <th key={i} style={mistableTh}>
                      {header.label}
                    </th>
                  ))}
                  <th style={mistableTh}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((item, index) => (
                    <tr key={item.id || index}>
                      <td style={tableTdStyles}>
                        {(pagination.page - 1) * postsPerPage + index + 1}
                      </td>
                      {headers.map((header, i) => {
                        const val = item[header.value];
                        if (header.value === "disposition_list" || Array.isArray(val)) {
                          return (
                            <td key={i} style={{ ...tableTdStyles, textAlign: "left", paddingLeft: "16px" }}>
                              {Array.isArray(val) && val.length > 0 ? (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", padding: "4px 0" }}>
                                  {val.map((dispo, idx) => (
                                    <span
                                      key={idx}
                                      style={{
                                        display: "inline-block",
                                        backgroundColor: "#f1f5f9",
                                        color: "#334155",
                                        border: "1px solid #e2e8f0",
                                        padding: "2px 8px",
                                        borderRadius: "12px",
                                        fontSize: "11.5px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {dispo}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <span style={{ color: "#94a3b8" }}>--</span>
                              )}
                            </td>
                          );
                        }
                        if (header.value === "name") {
                          return (
                            <td
                              key={i}
                              style={{
                                ...tableTdStyles,
                                textAlign: "left",
                                paddingLeft: "16px",
                                fontWeight: 700,
                                color: "#1e3a8a",
                              }}
                            >
                              {val || "--"}
                            </td>
                          );
                        }
                        return (
                          <td key={i} style={tableTdStyles}>
                            {val ?? "--"}
                          </td>
                        );
                      })}
                      <td style={tableTdStyles}>
                        <button
                          className="icon-btn edit-btn"
                          onClick={() => handleEdit(item)}
                          title="Edit Disposition Group"
                        >
                          <Edit size={18} color="#f4a261" />
                        </button>
                        <button
                          className="icon-btn delete-btn"
                          onClick={() => handleDeleteGroup(item)}
                          title="Delete Disposition Group"
                          style={{ marginLeft: "8px" }}
                        >
                          <Trash2 size={18} color="#e63946" />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={headers.length + 2} style={tableTdStyles}>
                      No disposition groups available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Add / Edit Modal */}
        {showModal && (
          <AddDispositionGroupModel
            isOpen={showModal}
            onClose={() => setShowModal(false)}
            onSave={handleSaveDispositionGroup}
            itemData={isEditing ? selectedItem : null}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <ConfirmDeleteModal
            onClose={() => {
              setShowDeleteModal(false);
              setDeleteTarget(null);
            }}
            onConfirm={handleConfirmDelete}
            itemName={deleteTarget?.name || "this disposition group"}
          />
        )}

        <Pagination
          pagination={pagination}
          postsPerPage={postsPerPage}
          handlePageSizeChange={handlePageSizeChange}
          fetchDetail={fetchDetail}
        />
    </>
  );
}
